import moment from 'moment';

import { IPortfolioPerformanceAccounts } from '../portfolio-performance.types';
import {
  BTC_USD_SYMBOL,
  COMBINED_USD_LABEL,
  COMBINED_USD_TOKEN,
  STABLECOIN_USD_SYMBOLS,
} from './risk.constants';
import { MetricKey, METRICS } from './risk.types';

export const computeTimeRange = (
  range: string,
): { begin: string; end: string; frequency: number } => {
  const end = moment.utc();
  const n = parseInt(range, 10);
  const suffix = range.replace(/^\d+/, '');
  const begin = end.clone();

  if (suffix === 'min') {
    begin.subtract(n, 'minutes');
  } else if (suffix === 'h') {
    begin.subtract(n, 'hours');
  } else if (suffix === 'D') {
    begin.subtract(n, 'days');
  } else if (suffix === 'W') {
    begin.subtract(n, 'weeks');
  } else if (suffix === 'M') {
    begin.subtract(n, 'months');
  } else {
    begin.subtract(1, 'days');
  }

  // LOCKSTEP: for the four RangeKeys the risk tab uses (1D/1W/1M/3M) the
  // frequency chosen below must match FREQUENCY_SECONDS in risk.constants.ts.
  // The initial grid is built here; the historical-append grid is built from
  // frequencySecondsFor(). The combined-USD element-wise sum only aligns
  // book-to-book when both request an identical frequency for a given range.
  let frequency: number;
  if (suffix === 'min' || suffix === 'h') {
    frequency = 10;
  } else if (suffix === 'D' && n === 1) {
    frequency = 60;
  } else if (suffix === 'D' && n === 3) {
    frequency = 180;
  } else if (suffix === 'D' && n === 5) {
    frequency = 300;
  } else if (suffix === 'D' && n === 15) {
    frequency = 900;
  } else if (suffix === 'W') {
    frequency = 900;
  } else if (suffix === 'M' && n === 3) {
    frequency = 7200;
  } else {
    frequency = 3600;
  }

  return {
    begin: begin.format('YYYY-MM-DDTHH:mm:ss[Z]'),
    end: end.format('YYYY-MM-DDTHH:mm:ss[Z]'),
    frequency,
  };
};

// Accounts don't carry a currency field directly, but the account name
// already follows the backend's own reporting-currency convention: a
// "| USD)" denomination marker means the account reports/risks in USD
// (QUOTE); everything else (BTC, ETH, etc. — e.g. "Ferro Vol Fund (BTC)",
// "Avenir (BTCUSD | BTC)") reports in the crypto itself (BASE). Deciding
// this synchronously off the name (rather than waiting on the async
// performance report) avoids defaulting to QUOTE for BTC-denominated books
// like Ferro Vol Fund.
//
// The marker is matched ANYWHERE, not just at end-of-string, so class/suffix-
// tagged names like "Pythagoras (BTCUSD | USD) (Class F)" are still USD. Only
// the "| USD)" marker counts — "(… | BTC)" and bare "(BTC)" stay BASE, and
// the leading "BTCUSD" in a pair label can't false-match (no preceding "|").
export const inferDefaultRiskTokenType = (accountName: string): 'QUOTE' | 'BASE' =>
  /\|\s*USD\s*\)/i.test(accountName) ? 'QUOTE' : 'BASE';

export const isStablecoinUsdSymbol = (symbol: string): boolean =>
  (STABLECOIN_USD_SYMBOLS as readonly string[]).includes(symbol.toUpperCase());

// A stablecoin book is pure cash: it contributes its PV (balance) to the
// combined total but zero risk, so every greek is summed from the
// non-stablecoin (BTC) book only. PV is the sole balance metric; everything
// else in METRICS is a greek.
export const symbolContributesToMetric = (symbol: string, metric: MetricKey): boolean =>
  metric === 'PV' || !isStablecoinUsdSymbol(symbol);

// The subset of constituent symbols that feed a given metric's combined series:
// all books for PV, the non-stablecoin books for greeks.
export const contributingSymbolsForMetric = (symbols: string[], metric: MetricKey): string[] =>
  symbols.filter((s) => symbolContributesToMetric(s, metric));

// The constituent settlement books to fold into the combined-USD view for a
// QUOTE-denominated account: the BTC-settled book plus any stablecoin-settled
// book present on the account. Requires a BTC book (it carries the risk the
// combined greeks are built from) and at least one stablecoin book to combine
// with; otherwise there is nothing to combine and [] is returned (BASE
// accounts, or a single-book account). Order is canonical: BTC first, then
// stablecoins in STABLECOIN_USD_SYMBOLS order.
export const getCombinedUsdSymbols = (account: IPortfolioPerformanceAccounts): string[] => {
  if (inferDefaultRiskTokenType(account.name) !== 'QUOTE') return [];
  const present = new Set(account.symbols.map((s) => s.toUpperCase()));
  if (!present.has(BTC_USD_SYMBOL)) return [];
  const combined = [BTC_USD_SYMBOL];
  for (const stable of STABLECOIN_USD_SYMBOLS) {
    if (present.has(stable)) combined.push(stable);
  }
  return combined.length >= 2 ? combined : [];
};

// Combine one live tick across constituent books into a single metrics object.
// When cashPvOnly is set (the combined-USD view) a stablecoin book adds only to
// PV and nothing to the greeks; otherwise every book contributes to every
// metric (the single-book case, where the caller wants that book's own risk).
export const combineConstituentMetrics = (
  perSymbol: { symbol: string; metrics: Record<MetricKey, number> }[],
  cashPvOnly: boolean,
): Record<MetricKey, number> => {
  const out = {} as Record<MetricKey, number>;
  for (const metric of METRICS) {
    let sum = 0;
    for (const { symbol, metrics } of perSymbol) {
      if (cashPvOnly && !symbolContributesToMetric(symbol, metric)) continue;
      sum += metrics[metric] ?? 0;
    }
    out[metric] = sum;
  }
  return out;
};

// Element-wise sum of N metric series onto the union of their timestamps. The
// constituent books are requested on an identical begin/end/frequency grid, so
// in the common case their samples co-occur and this is a plain point-wise sum.
//
// The metrics summed here (PV balance, greeks) are *levels*, not flows: between
// two samples a book's contribution is "unchanged", not "zero". So an interior
// gap in one book carries that book's last value forward rather than dropping
// it — without this, a single missing cash sample would momentarily subtract
// the whole balance and put a false sawtooth in the combined PV line. Before a
// book's first sample it contributes nothing (leading absence stays absent);
// after its last sample its final level is held. Output is sorted ascending by
// timestamp, matching the single-series wire order.
export const sumAlignedSeries = (
  seriesList: ([number, number][] | null | undefined)[],
): [number, number][] => {
  const books = seriesList.filter((s): s is [number, number][] => !!s && s.length > 0);
  if (books.length === 0) return [];

  const timestamps = Array.from(new Set(books.flatMap((b) => b.map(([ts]) => ts)))).sort(
    (a, b) => a - b,
  );
  const byTs = books.map((b) => new Map(b));
  const firstTs = books.map((b) => b.reduce((min, [ts]) => (ts < min ? ts : min), Infinity));
  const carry = new Array<number>(books.length).fill(0);

  return timestamps.map((ts) => {
    let sum = 0;
    for (let i = 0; i < books.length; i += 1) {
      if (ts < firstTs[i]) continue; // leading absence — contributes nothing yet
      const value = byTs[i].get(ts);
      if (value !== undefined) carry[i] = value; // new sample updates the level...
      sum += carry[i]; // ...otherwise hold the last one across the gap
    }
    return [ts, sum] as [number, number];
  });
};

const TOKEN_SUFFIXES = ['USDC', 'USDT', 'USD'];

export const formatTokenName = (token: string): string => {
  if (token === COMBINED_USD_TOKEN) return COMBINED_USD_LABEL;
  for (const suffix of TOKEN_SUFFIXES) {
    if (token.endsWith(suffix)) {
      const base = token.slice(0, token.length - suffix.length);
      return `${base} ${suffix}`;
    }
  }
  return token;
};

// Just the base symbol (e.g. "BTCUSD" -> "BTC") — for unit labels on
// base-currency-denominated figures (live-risk-band's PV/Delta/Theta/
// Gamma/Vega tiles), where formatTokenName's "BTC USD" pair-name form
// isn't the right shape.
export const getBaseSymbol = (token: string): string => {
  for (const suffix of TOKEN_SUFFIXES) {
    if (token.endsWith(suffix)) {
      return token.slice(0, token.length - suffix.length);
    }
  }
  return token;
};
